import { observer } from 'mobx-react-lite';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { chatDebugStore } from '@/features/chat-debug/chat-debug.store';
import {
  ZERO_COST_BREAKDOWN,
  sumCostBreakdowns,
  type ContextBreakdownItem,
  type CostBreakdown,
} from '@/features/chat-debug/chat-debug.types';
import {
  CostSummaryTable,
  formatUsd as formatUsd6,
} from '@/features/chat-debug/llm-costs/llm-costs.viewer';
import { classList } from '@/utils/cn';

import type { ChatCostLiveStore } from './chat-cost-live.store';

export const CTX_WARN_PCT = 70;
export const CTX_DANGER_PCT = 90;
const CONTEXT_RING_RADIUS = 10;
const CONTEXT_RING_CIRCUMFERENCE = 2 * Math.PI * CONTEXT_RING_RADIUS;
const CONTEXT_RING_SIZE = 24;

export const FOOTER_TESTIDS = {
  root: 'composer-cost-footer',
  promptTokens: 'cost-prompt-tokens',
  outputTokens: 'cost-output-tokens',
  cacheReadTokens: 'cost-cache-read-tokens',
  cacheWriteTokens: 'cost-cache-write-tokens',
  totalUsd: 'cost-total-usd',
  contextPercent: 'cost-context-percent',
  model: 'cost-model',
} as const;

export interface ComposerCostFooterProps {
  readonly store: ChatCostLiveStore;
  readonly className?: string;
  readonly reserveSpace?: boolean;
}

function formatTokens(count: number): string {
  if (count === 0) return '0';
  const sign = count < 0 ? '-' : '';
  const abs = Math.abs(count);
  if (abs >= 1_000_000) {
    const m = abs / 1_000_000;
    if (Number.isInteger(m)) return `${sign}${m}M`;
    return `${sign}${m >= 10 ? m.toFixed(1) : m.toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    const k = abs / 1_000;
    if (Number.isInteger(k)) return `${sign}${k}k`;
    return `${sign}${k >= 10 ? k.toFixed(1) : k.toFixed(2)}k`;
  }
  return count.toString();
}

function formatUsd3(amount: number): string {
  if (amount === 0) return '$0.000';
  if (amount < 0.001) return '<$0.001';
  return `$${amount.toFixed(3)}`;
}

const CONTEXT_COLORS: Record<
  ContextBreakdownItem['kind'],
  { readonly dot: string; readonly bar: string }
> = {
  system: { dot: 'bg-zinc-400', bar: 'bg-zinc-400' },
  system_static: { dot: 'bg-zinc-500', bar: 'bg-zinc-500' },
  system_dynamic: { dot: 'bg-sky-400', bar: 'bg-sky-400' },
  system_field: { dot: 'bg-zinc-500', bar: 'bg-zinc-500' },
  tools: { dot: 'bg-purple-400', bar: 'bg-purple-400' },
  tool_definition: { dot: 'bg-purple-400', bar: 'bg-purple-400' },
  conversation: { dot: 'bg-rose-400', bar: 'bg-rose-400' },
  user_message: { dot: 'bg-green-400', bar: 'bg-green-400' },
  assistant_message: { dot: 'bg-blue-400', bar: 'bg-blue-400' },
  thoughts: { dot: 'bg-amber-400', bar: 'bg-amber-400' },
  tool_call: { dot: 'bg-cyan-400', bar: 'bg-cyan-400' },
  tool_result: { dot: 'bg-orange-400', bar: 'bg-orange-400' },
  compaction_summary: { dot: 'bg-indigo-400', bar: 'bg-indigo-400' },
};

function contextColor(kind: ContextBreakdownItem['kind']) {
  return CONTEXT_COLORS[kind];
}

// Token counts + total USD, shared by the chat composer footer and the
// internal routine-run cost footer so both read identically.
export function CostTokensStrip({
  breakdown,
  totalUsd,
}: {
  breakdown: CostBreakdown;
  totalUsd: number;
}): ReactNode {
  return (
    <>
      <span
        className="flex items-center gap-1 tabular-nums"
        title="Fresh input tokens (billed at input rate)"
      >
        <Icons.arrowUp className="w-3 h-3" aria-hidden />
        <span data-testid={FOOTER_TESTIDS.promptTokens}>
          {formatTokens(breakdown.fresh_input_tokens)}
        </span>
      </span>
      <span
        className="flex items-center gap-1 tabular-nums"
        title="Output tokens"
      >
        <Icons.arrowDown className="w-3 h-3" aria-hidden />
        <span data-testid={FOOTER_TESTIDS.outputTokens}>
          {formatTokens(breakdown.output_tokens)}
        </span>
      </span>
      <span
        className="flex items-center gap-1 tabular-nums"
        title="Cache reads (cheap reuse of prior prompt)"
      >
        <span aria-hidden className="text-[10px] font-semibold">
          R
        </span>
        <span data-testid={FOOTER_TESTIDS.cacheReadTokens}>
          {formatTokens(breakdown.cached_read_tokens)}
        </span>
      </span>
      <span
        className="flex items-center gap-1 tabular-nums"
        title="Cache writes (one-time cost to cache prompt)"
      >
        <span aria-hidden className="text-[10px] font-semibold">
          W
        </span>
        <span data-testid={FOOTER_TESTIDS.cacheWriteTokens}>
          {formatTokens(breakdown.cache_write_tokens)}
        </span>
      </span>
      <span
        data-testid={FOOTER_TESTIDS.totalUsd}
        className="font-medium text-foreground-subtle tabular-nums"
      >
        {formatUsd3(totalUsd)}
      </span>
    </>
  );
}

function contextPercentColor(pct: number): string {
  if (pct >= CTX_DANGER_PCT) return 'text-red-500';
  if (pct >= CTX_WARN_PCT) return 'text-amber-500';
  return 'text-foreground-muted';
}

function totalBreakdownTokens(rows: ContextBreakdownItem[]): number {
  return rows.reduce((total, row) => total + row.token_count, 0);
}

// Largest-remainder rounding so the integer percentages always sum to 100.
function distributePercentages(values: readonly number[]): number[] {
  const total = values.reduce((sum, value) => sum + Math.max(0, value), 0);
  if (total <= 0) return values.map(() => 0);
  const raw = values.map((value) => (Math.max(0, value) / total) * 100);
  const floors = raw.map((value) => Math.floor(value));
  const leftover = 100 - floors.reduce((sum, value) => sum + value, 0);
  const fractionRanking = raw
    .map((value, index) => ({ index, fraction: value - floors[index] }))
    .sort((a, b) => b.fraction - a.fraction);
  for (let i = 0; i < leftover && i < fractionRanking.length; i += 1) {
    floors[fractionRanking[i].index] += 1;
  }
  return floors;
}

function ContextProgressRing({ pct }: { pct: number }): ReactNode {
  const boundedPct = Math.max(0, Math.min(100, pct));
  const strokeDashoffset = CONTEXT_RING_CIRCUMFERENCE * (1 - boundedPct / 100);

  return (
    <span
      className={classList(
        'relative inline-flex items-center justify-center',
        contextPercentColor(pct)
      )}
      style={{ width: CONTEXT_RING_SIZE, height: CONTEXT_RING_SIZE }}
      aria-label={`${boundedPct.toFixed(0)}% context full`}
    >
      <svg
        className="absolute inset-0 size-full"
        viewBox="0 0 32 32"
        aria-hidden
      >
        <circle
          cx="16"
          cy="16"
          r={CONTEXT_RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeOpacity="0.16"
          strokeWidth="3"
        />
        <circle
          cx="16"
          cy="16"
          r={CONTEXT_RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeWidth="3"
          strokeDasharray={CONTEXT_RING_CIRCUMFERENCE}
          strokeDashoffset={strokeDashoffset}
          transform="rotate(-90 16 16)"
        />
      </svg>
    </span>
  );
}

function ContextBreakdownRow({
  row,
  depth = 0,
  displayValue,
}: {
  row: ContextBreakdownItem;
  depth?: number;
  // Override the right-hand value (e.g. `42%` for conversation children).
  // When omitted, fall back to the row's absolute token count.
  displayValue?: string;
}): ReactNode {
  const [isOpen, setIsOpen] = useState(
    depth === 0 || row.kind === 'system_dynamic'
  );
  const hasChildren = row.children.length > 0;
  const colors = contextColor(row.kind);
  // Conversation children are shown as percentages (rounded to sum to 100) since
  // the per-bucket counts are local approximations and the absolute numbers can
  // mislead. Inputs / outputs / tool calls / tool results / thoughts together
  // make up the conversation row.
  const childPercentages =
    row.kind === 'conversation'
      ? distributePercentages(row.children.map((child) => child.token_count))
      : null;

  return (
    <div>
      <button
        type="button"
        className={classList(
          'flex w-full items-center gap-2 rounded px-1 py-0.5 text-left',
          hasChildren && 'hover:bg-surface-hover'
        )}
        style={{ paddingLeft: `${depth * 12 + 4}px` }}
        onClick={() => hasChildren && setIsOpen((value) => !value)}
      >
        {hasChildren ? (
          <Icons.chevronRight
            className={classList(
              'size-3 shrink-0 text-foreground-subtle transition-transform',
              isOpen && 'rotate-90'
            )}
            aria-hidden
          />
        ) : (
          <span className="size-3 shrink-0" aria-hidden />
        )}
        <span className={classList('size-2 rounded-sm shrink-0', colors.dot)} />
        <span className="min-w-0 flex-1 truncate text-foreground-subtle">
          {row.label}
        </span>
        <span className="shrink-0 tabular-nums text-foreground-muted">
          {displayValue ??
            (row.kind === 'conversation' && row.token_count < 0
              ? '~0'
              : formatTokens(row.token_count))}
        </span>
      </button>
      {hasChildren && isOpen && (
        <div>
          {row.children.map((child, index) => (
            <ContextBreakdownRow
              key={`${child.kind}-${child.label}-${index}`}
              row={child}
              depth={depth + 1}
              displayValue={
                childPercentages ? `${childPercentages[index]}%` : undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function ContextBreakdownPanel({
  tokens,
  window,
  breakdown,
}: {
  tokens: number;
  window: number;
  breakdown: ContextBreakdownItem[];
}): ReactNode {
  const usedTokens = tokens;
  const breakdownTokens = totalBreakdownTokens(breakdown);
  const pct = (usedTokens / window) * 100;

  return (
    <div className="w-[320px] text-xs text-foreground-subtle">
      <div className="flex items-start justify-between gap-4 pb-2">
        <div>
          <div className="font-medium text-foreground">Context</div>
          <div className="text-[11px] text-foreground-muted">
            {pct.toFixed(0)}% Full
          </div>
        </div>
        <div className="pt-5 text-[11px] tabular-nums text-foreground-muted">
          {formatTokens(usedTokens)} / {formatTokens(window)} Tokens
        </div>
      </div>

      <div className="mb-3 flex h-1 overflow-hidden rounded-full bg-border">
        {breakdown.map((row, index) => {
          const rowShare = breakdownTokens
            ? row.token_count / breakdownTokens
            : row.token_count / window;
          const widthPct = Math.max(0.5, rowShare * pct);
          return (
            <div
              key={`${row.kind}-${row.label}-${index}`}
              className={contextColor(row.kind).bar}
              style={{ width: `${widthPct}%` }}
            />
          );
        })}
      </div>

      <div className="space-y-1">
        {breakdown.length ? (
          breakdown.map((row, index) => (
            <ContextBreakdownRow
              key={`${row.kind}-${row.label}-${index}`}
              row={row}
            />
          ))
        ) : (
          <div className="text-foreground-muted">
            {formatTokens(tokens)} prompt tokens
          </div>
        )}
      </div>
    </div>
  );
}

function ContextBadge({
  tokens,
  window,
  breakdown,
}: {
  tokens: number;
  window: number | null;
  breakdown: ContextBreakdownItem[];
}): ReactNode {
  if (!window || window <= 0) {
    return (
      <TooltipPrimitive.Root delayDuration={150}>
        <TooltipPrimitive.Trigger asChild>
          <span
            data-testid={FOOTER_TESTIDS.contextPercent}
            className="text-foreground-muted tabular-nums"
          >
            --%
          </span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            className={classList(
              'z-50',
              'rounded-lg smoothing-default',
              'border border-border-strong bg-surface shadow-sm',
              'p-3 text-xs text-foreground-subtle'
            )}
          >
            Unknown context window
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    );
  }
  const pct = (tokens / window) * 100;
  return (
    <TooltipPrimitive.Root delayDuration={150}>
      <TooltipPrimitive.Trigger asChild>
        <span
          data-testid={FOOTER_TESTIDS.contextPercent}
          className="inline-flex items-center"
        >
          <ContextProgressRing pct={pct} />
        </span>
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="top"
          sideOffset={6}
          className={classList(
            'z-50',
            'rounded-lg smoothing-default',
            'border border-border-strong bg-surface shadow-sm',
            'p-3 text-xs text-foreground-subtle'
          )}
        >
          <ContextBreakdownPanel
            tokens={tokens}
            window={window}
            breakdown={breakdown}
          />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}

export const ComposerCostFooter = observer(function ComposerCostFooter({
  store,
  className,
  reserveSpace,
}: ComposerCostFooterProps): ReactNode {
  const { activeChatId, summary, latestStep } = store;
  if (!activeChatId || !summary) {
    return reserveSpace ? <div className={className} /> : null;
  }
  const grandTotalUsd = summary.grand_total_usd ?? 0;

  const turn = summary.turns.at(-1)?.total_cost ?? ZERO_COST_BREAKDOWN;
  const turnsTotal = summary.turns.reduce(
    (acc, t) => sumCostBreakdowns(acc, t.total_cost),
    ZERO_COST_BREAKDOWN
  );
  const chatWide = summary.compaction_total
    ? sumCostBreakdowns(turnsTotal, summary.compaction_total)
    : turnsTotal;
  // Context "used" = full input the provider saw (fresh + cache reads + cache writes)
  // plus this turn's output. Output is already-spent budget because the next turn's
  // history will include it. Computed from the explicit components instead of the
  // derived `total_prompt_tokens` so the math is unambiguous across providers.
  const contextUsedTokens =
    (latestStep?.fresh_input_tokens ?? 0) +
    (latestStep?.cached_read_tokens ?? 0) +
    (latestStep?.cache_write_tokens ?? 0) +
    (latestStep?.output_tokens ?? 0);
  const contextWindow = latestStep?.context_window ?? null;
  const contextBreakdown = latestStep?.context_breakdown ?? [];
  const latestModel = latestStep?.model ?? null;

  const handleOpenDebug = (): void => {
    chatDebugStore.setActiveChatId(activeChatId);
    const session = chatDebugStore.active;
    if (!session) return;
    if (session.messages.length === 0) {
      session.fetchTracesForChat();
    }
    session.setIsOpen(true);
  };

  return (
    <div
      data-testid={FOOTER_TESTIDS.root}
      data-chat-id={activeChatId}
      data-total-usd={grandTotalUsd.toFixed(6)}
      className={classList(
        'flex flex-row items-center justify-between',
        'px-3 py-1 gap-4',
        'text-[11px] text-foreground-muted',
        'select-none',
        className
      )}
    >
      <TooltipPrimitive.Root delayDuration={150}>
        <TooltipPrimitive.Trigger asChild>
          <button
            type="button"
            onClick={handleOpenDebug}
            className={classList(
              'flex flex-row items-center gap-3',
              'hover:text-foreground-subtle transition-colors',
              'cursor-pointer'
            )}
            aria-label="Open chat cost details"
          >
            <CostTokensStrip breakdown={chatWide} totalUsd={grandTotalUsd} />
          </button>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={6}
            className={classList(
              'z-50',
              'rounded-lg smoothing-default',
              'border border-border-strong bg-surface shadow-sm',
              'p-3 text-xs text-foreground-subtle',
              'min-w-[280px]'
            )}
          >
            <div className="flex items-center justify-between gap-4 pb-2 text-[11px] text-foreground-muted">
              <span data-testid={FOOTER_TESTIDS.model} className="truncate">
                {latestModel ?? 'Live cost'} · this turn
              </span>
              <span className="tabular-nums">{formatUsd6(turn.total_usd)}</span>
            </div>
            <CostSummaryTable breakdown={turn} />
            <div className="text-[10px] text-foreground-muted pt-2">
              Click to open cost details in the debug sidebar.
            </div>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>

      <div className="flex items-center gap-1">
        <ContextBadge
          tokens={contextUsedTokens}
          window={contextWindow}
          breakdown={contextBreakdown}
        />
      </div>
    </div>
  );
});
